// Billing runs: creating, changing and deleting them.
//
// Moved out of pages/finance/BillingRun.js. Every function takes the Supabase
// client as its first argument rather than importing it, so the write paths
// can be pinned in tests with src/testUtils/supabaseMock.js. How a visit
// becomes a line is lib/billing/claimLines.js; this is only what is written.

import { destinationForPayer } from '../serviceModel.js';
import { clientOf, buildClaimLine, runTotals } from './claimLines.js';
import { runPaidFromLines } from '../edi/remitPosting.js';
import { resyncLineUpdate } from './resync.js';
import { must } from './must.js';

/**
 * A week is built the same way for every payer and differs only in where it
 * files, so it is split by destination and nothing else: one GAMMIS run for
 * GAPP and FFS Medicaid, one CareSource run if any CareSource client had
 * visits. A CareSource run never sees the 837P button or the transport; it is
 * exported, filed on CareSource's side, and its claim number recorded here.
 *
 * @returns {Object<string, object[]>} destination -> visits, in first-seen order
 */
export const groupByDestination = (visits = [], clientMap = {}) => {
  const byDest = {};
  for (const v of visits) (byDest[destinationForPayer(clientOf(v, clientMap).payer)] ||= []).push(v);
  return byDest;
};

export const CARESOURCE_RUN_NOTE = 'CareSource run — valued on the Medicaid fee schedule until a CareSource rate is on file. Filed with CareSource outside CAERA; record the claim number here.';

// ── Reading ─────────────────────────────────────────────────────────────────

export const loadRuns = async (sb) => {
  const { data } = await sb.from('billing_runs').select('*').order('date_from', { ascending: false });
  return data || [];
};

export const loadRunLines = async (sb, runId) => {
  const { data } = await sb.from('claim_lines').select('*').eq('billing_run_id', runId).order('service_date');
  return data || [];
};

// ── Creating ────────────────────────────────────────────────────────────────

/**
 * Create one approved run per destination from the selected visits, and its
 * claim lines. Lines are built by lib/billing/claimLines.js exactly as the
 * Monday run and resync build them.
 *
 * Throws on the first failed insert. (The run row is written before its
 * lines, so a failure between the two leaves a run with no lines.)
 *
 * @returns {object[]} the created runs, each with `_lines` (its line count)
 */
export const createRunsFromVisits = async (sb, {
  visits, clientMap, authHist, catalog, basis, multiplier, dateFrom, dateTo, profileId = null, now,
}) => {
  const byDest = groupByDestination(visits, clientMap);
  const created = [];
  for (const [dest, group] of Object.entries(byDest)) {
    const claimRows = group.map(v => {
      const hc = clientOf(v, clientMap);
      return buildClaimLine({
        visit: v, client: hc, authHistory: authHist[hc.id] || [], catalog,
        basis, multiplier,
      });
    });
    const totals = runTotals(claimRows);
    const { data: run, error: runErr } = await sb.from('billing_runs').insert({
      date_from: dateFrom, date_to: dateTo, status: 'approved',
      payer: dest,
      created_by: profileId, approved_by: profileId,
      approved_at: now,
      total_lines: totals.lines,
      total_accrued_amount: totals.accrued,
      total_charge_amount: totals.charge,
      charge_multiplier: multiplier,
      hours_basis: basis,
      notes: dest === 'caresource' ? CARESOURCE_RUN_NOTE : null,
    }).select().single();
    if (runErr) throw runErr;

    const { error: linesErr } = await sb.from('claim_lines')
      .insert(claimRows.map(r => ({ ...r, billing_run_id: run.id })));
    if (linesErr) throw linesErr;
    // There used to be a "safety backfill" here that matched any line
    // still missing a Medicaid ID to a client by name, across every
    // record including tests and duplicates. That is exactly the match
    // the shared rule now refuses; a line with no client shows in the run
    // and in its self-audit for a person to resolve.
    created.push({ ...run, _lines: group.length });
  }
  return created;
};

// ── Changing ────────────────────────────────────────────────────────────────

/**
 * Add the new visits and re-value the changed ones a resync found, then
 * re-total the run and move its dates to the range resynced. Lines added by a
 * resync bill on the basis and multiplier the run was created with, so a run
 * stays internally consistent no matter when a line joined it.
 *
 * @returns {{ added: number, updated: number }}
 */
export const applyResync = async (sb, {
  run, items, clientMap, authHist, catalog, dateFrom, dateTo, defaultMultiplier, now,
}) => {
  const basis = run?.hours_basis || 'scheduled';
  // Checked again here, where the dates are written, not only when the modal
  // fetched: another run could have been created for those days since.
  const clash = await runsNewlyOverlapped(sb, { run, dateFrom, dateTo });
  if (clash.length) {
    throw new Error(`these dates overlap the run for ${clash.map(r => `${r.date_from} – ${r.date_to}`).join(', ')}. Nothing was changed.`);
  }
  let added = 0; let updated = 0;
  for (const item of items) {
    const { visit: v, status, existingLine } = item;
    const hc = clientOf(v, clientMap);
    // Built exactly as the wizard and the Monday run build a line, on the
    // run's own basis and multiplier.
    const row = buildClaimLine({
      visit: v, client: hc, authHistory: authHist[hc.id] || [], catalog,
      basis, multiplier: run.charge_multiplier || defaultMultiplier,
      note: 'resynced',
    });
    if (status === 'new') {
      const { error } = await sb.from('claim_lines').insert({ ...row, billing_run_id: run.id });
      if (error) throw error;
      added++;
    } else if (status === 'updated' && existingLine) {
      // An update changes what the visit is worth, never who it is for or
      // the authorisation it was already given.
      const { error } = await sb.from('claim_lines')
        .update(resyncLineUpdate(row, existingLine, now))
        .eq('id', existingLine.id);
      if (error) throw error;
      updated++;
    }
  }

  // Update billing_run date range + totals
  const refreshed = await must(
    sb.from('claim_lines').select('accrued_amount, charge_amount').eq('billing_run_id', run.id),
    "re-read the run's lines");
  const totals = runTotals(refreshed || []);
  await must(sb.from('billing_runs').update({
    date_from: dateFrom, date_to: dateTo,
    total_lines: totals.lines,
    total_accrued_amount: totals.accrued,
    total_charge_amount:  totals.charge,
  }).eq('id', run.id), "re-total the run");
  return { added, updated };
};

/**
 * Other runs for the same destination that a resync would newly overlap if it
 * moved this run's dates to [dateFrom, dateTo]. Resync used to move them with
 * no check, so widening a run could take in days another run already billed —
 * the same care on two runs. A run that already overlaps another (a test run
 * inside a real week) is not refused for staying where it is, but is refused
 * for widening further into it. applyResync checks this again before writing.
 */
export const runsNewlyOverlapped = async (sb, { run, dateFrom, dateTo }) => {
  const others = await must(sb.from('billing_runs').select('id, date_from, date_to, payer')
    .lte('date_from', dateTo).gte('date_to', dateFrom).neq('id', run.id), 'check for overlapping runs');
  const dest = run.payer || 'gammis';
  // The days the two runs would share after the resync. A run is refused only
  // for shared days it did not already share — so an existing overlap may
  // stay, but cannot be widened further into the other run.
  const sharesOnlyOldDays = (r) => {
    const lo = r.date_from > dateFrom ? r.date_from : dateFrom;
    const hi = r.date_to < dateTo ? r.date_to : dateTo;
    return lo >= run.date_from && hi <= run.date_to;
  };
  return (others || []).filter(r => (r.payer || 'gammis') === dest && !sharesOnlyOldDays(r));
};

/** A run's paid total, summed from its lines — every payment it has had, from any source. */
export const refreshRunPaid = async (sb, runId) => {
  const paidLines = await must(
    sb.from('claim_lines').select('paid_amount').eq('billing_run_id', runId), "re-read the run's payments");
  await must(sb.from('billing_runs').update({ total_paid_amount: runPaidFromLines(paidLines || []) }).eq('id', runId),
    "update the run's paid total");
};

/**
 * Save the line-edit form. A payment corrected by hand moves the run's paid
 * total with it.
 *
 * @returns {object} the columns written, for the caller to merge into its copy
 */
export const saveLineEdit = async (sb, { lineId, runId, form, now }) => {
  const upd = {
    paid_amount: form.paid_amount ? parseFloat(form.paid_amount) : null,
    allowed_amount: form.allowed_amount ? parseFloat(form.allowed_amount) : null,
    icn: form.icn || null, paid_date: form.paid_date || null,
    denial_code: form.denial_code || null, denial_reason: form.denial_reason || null,
    prior_auth_number: form.prior_auth_number || null, notes: form.notes || null,
    updated_at: now,
  };
  await must(sb.from('claim_lines').update(upd).eq('id', lineId), 'save the line');
  if (runId) await refreshRunPaid(sb, runId);
  return upd;
};

/**
 * What to ask before the status picker changes a line. The picker used to
 * write the moment it moved — on any run, closed ones included — so one stray
 * click changed a claim's recorded outcome with nothing to say it happened.
 */
export const lineStatusQuestion = ({ line, run, status }) => [
  `Change ${line.client_name || 'this line'} on ${line.service_date || 'no date'} from ${line.status} to ${status}?`,
  status === 'paid' ? 'The paid date will be set to today. The paid amount does not change — record it under Payment and corrections.' : null,
  run && run.status !== 'approved' ? `This run is ${run.status}: its line statuses normally come from GAMMIS, not from here.` : null,
].filter(Boolean).join('\n\n');

/**
 * The status picker on a line. Marking a line paid stamps today's date.
 * Throws when the write is refused, so the caller can say so.
 * @returns {object} the columns written
 */
export const setLineStatus = async (sb, { lineId, status, today, now }) => {
  const upd = { status, updated_at: now };
  if (status === 'paid') upd.paid_date = today;
  const { error } = await sb.from('claim_lines').update(upd).eq('id', lineId);
  if (error) throw new Error(`Could not change the line's status: ${error.message}`);
  return upd;
};

/**
 * Remove one line and re-total the run from the lines that remain.
 * `remaining` is the caller's copy of the run's other lines.
 */
export const removeLine = async (sb, { lineId, runId, remaining }) => {
  await must(sb.from('claim_lines').delete().eq('id', lineId), 'remove the line');
  const newAccrued = remaining.reduce((s, l) => s + (l.accrued_amount || 0), 0);
  const newCharge  = remaining.reduce((s, l) => s + (l.charge_amount  || 0), 0);
  await must(sb.from('billing_runs').update({
    total_lines: remaining.length,
    total_accrued_amount: parseFloat(newAccrued.toFixed(2)),
    total_charge_amount:  parseFloat(newCharge.toFixed(2)),
  }).eq('id', runId), 're-total the run');
};

// Why a run has to stay. A run's claim_submissions are deleted with it and
// its remittances lose their run, so deleting one whose file went to GAMMIS
// erased the only record that it went, and deleting one with a payment posted
// left the payment attached to nothing — both reachable, because a run
// reopens to 'approved' when GAMMIS rejects its file. The database refuses the
// same deletes (billing_run_delete_guard); these say why before it has to.
export const RUN_KEPT_SENT = 'This run has a claim file that was sent to GAMMIS, so it cannot be deleted. Its filing record has to stay.';
export const RUN_KEPT_PAID = 'This run has a remittance posted to it, so it cannot be deleted. The payment would be left attached to nothing.';

/** The reason a run may not be deleted, or null when it may. Throws if it cannot tell. */
export const whyRunIsKept = async (sb, runId) => {
  const { data: subs, error: subErr } = await sb.from('claim_submissions')
    .select('status, sent_at').eq('billing_run_id', runId);
  if (subErr) throw new Error(`Could not check the run's claim files: ${subErr.message}`);
  if ((subs || []).some(x => x.sent_at || x.status !== 'generated')) return RUN_KEPT_SENT;
  const { data: remits, error: remitErr } = await sb.from('remittances')
    .select('id').eq('billing_run_id', runId).limit(1);
  if (remitErr) throw new Error(`Could not check the run's remittances: ${remitErr.message}`);
  if ((remits || []).length) return RUN_KEPT_PAID;
  return null;
};

/**
 * Delete a run, unless it has to stay. One statement: its lines, and any file
 * generated but never sent, go with it by cascade, so a failure cannot leave
 * a run with half its lines.
 *
 * @returns {{ deleted: true } | { deleted: false, reason: string }}
 */
export const deleteRun = async (sb, runId) => {
  const reason = await whyRunIsKept(sb, runId);
  if (reason) return { deleted: false, reason };
  const { error } = await sb.from('billing_runs').delete().eq('id', runId);
  if (error) throw new Error(`Could not delete the run: ${error.message}`);
  return { deleted: true };
};

/**
 * Record the ICN (GAMMIS) or claim number (CareSource) returned for a run filed
 * by hand, and mark it and its approved lines submitted.
 */
export const recordClaimReference = async (sb, { runId, reference, now }) => {
  await must(sb.from('billing_runs').update({
    gammis_batch_ref: reference, status: 'submitted',
    submitted_at: now,
  }).eq('id', runId), 'record the claim reference');
  await must(sb.from('claim_lines').update({ status: 'submitted' })
    .eq('billing_run_id', runId).eq('status', 'approved'), "mark the run's lines submitted");
};
